import type { Tone } from './ui';

const CI: Record<string, string> = { pending: 'CI 대기', success: 'CI 통과', failure: 'CI 실패', unknown: 'CI —' };
const BOT_STEP: Record<string, string> = { open: 'dev PR 열림', merged: 'main 머지', closed: '닫힘' };
const ERROR: Record<string, string> = {
  GROUP_FULL: '정원 초과', BRANCH_EXISTS: '브랜치 이름 충돌', NOTHING_TO_ADD: '추가할 항목 없음', MANIFEST_NOT_FOUND: 'manifest 파일 없음',
  GITHUB_ERROR: 'GitHub 오류', FOREIGN_COMMIT: '외부 커밋 포함', PROMOTE_BLOCKED: 'promote 차단', PROMOTE_STALLED: '응답 없음', PROD_PR_CLOSED: 'prod PR 닫힘',
  BOT_PR_CLOSED: 'BOT PR 닫힘', COMMIT_STALLED: '커밋 정지 · 다시 시도 필요',
};

export type LabelInput = {
  status: string; ciState?: string | null; mergeable?: string | null; botPrNumber?: number | null; botPrState?: string | null; errorCode?: string | null;
};

/** 설계 14.7 의 배지 문구. 서버가 합성한 화면 상태(display_status)를 사람이 읽는 한 줄로 바꾼다 */
export function manifestLabel(b: LabelInput): string {
  switch (b.status) {
    case 'waiting': return 'PR 대기';
    case 'confirming': case 'committing': return b.errorCode === 'COMMIT_STALLED' ? ERROR.COMMIT_STALLED : '확정 중';
    case 'pr_open': return b.mergeable === 'dirty' ? '충돌' : `PR 열림 · ${CI[b.ciState ?? 'unknown'] ?? 'CI —'}`;
    case 'main_merged': return 'main 머지 · promote 요청 필요';
    case 'promote_requested': return 'promote 요청';
    case 'promote_pr_open': return b.errorCode === 'PROD_PR_CLOSED' ? 'promote 차단 · prod PR 닫힘' : 'promote PR 열림';
    case 'prod_merged': return 'prod 반영';
    // done 은 prod 반영 뒤에만 쓴다. 예전 BOT_PR_CLOSED 로 끝난 행은 반영이 아니므로 그렇게 보이지 않게 한다
    case 'done': return b.errorCode ? '종료' : 'prod 반영';
    case 'promote_blocked': return `promote 차단${b.errorCode && ERROR[b.errorCode] ? ` · ${ERROR[b.errorCode]}` : ''}`;
    case 'foreign_commit': return '외부 커밋 — 확인 필요';
    case 'bot_linked': return `BOT PR #${b.botPrNumber ?? '?'} · ${BOT_STEP[b.botPrState ?? ''] ?? b.botPrState ?? '—'}`;
    case 'already_in_manifest': return 'manifest에 이미 있음';
    case 'enqueue_skipped': return 'manifest 연결 생략';
    case 'failed': return `실패: ${(b.errorCode && ERROR[b.errorCode]) ?? b.errorCode ?? '알 수 없음'}`;
    case 'closed': return 'PR 닫힘 (재대기)';
    default: return b.status;
  }
}

export type ManifestFilter = '' | 'waiting' | 'progress' | 'blocked' | 'failed';
// main_merged 는 자동 /promote 가 꺼져 있으면 사람이 요청해야 넘어가는 "조치 필요" 상태라 진행 중이 아니라 차단 쪽이다
const PROGRESS = new Set(['confirming', 'committing', 'pr_open', 'promote_requested', 'promote_pr_open', 'bot_linked']);
const BLOCKED = new Set(['promote_blocked', 'foreign_commit', 'main_merged']);
/** 목록 필터: PR 대기 · 진행 중 · 차단·실패·조치 필요. prod PR 이 닫힌 promote_pr_open 과 main_merged 는 차단 쪽으로 센다 */
export function matchesManifestFilter(f: ManifestFilter, b?: LabelInput): boolean {
  if (!f) return true;
  if (!b) return false;
  const blocked = BLOCKED.has(b.status) || (b.status === 'promote_pr_open' && b.errorCode === 'PROD_PR_CLOSED') || b.errorCode === 'COMMIT_STALLED';
  if (f === 'waiting') return b.status === 'waiting';
  if (f === 'failed') return b.status === 'failed';
  if (f === 'blocked') return blocked;
  return PROGRESS.has(b.status) && !blocked;
}

/** 배지 톤: 실패=적 · 차단(조치 필요)=황 · prod 반영=녹 · 대기·진행=청 */
export function manifestTone(b: LabelInput): Tone {
  if (b.status === 'failed') return 'error';
  if (matchesManifestFilter('blocked', b)) return 'warn';
  if (b.status === 'prod_merged' || (b.status === 'done' && !b.errorCode)) return 'ok';
  return b.status === 'waiting' || matchesManifestFilter('progress', b) ? 'info' : 'neutral';
}

export const MANIFEST_STEPS = ['등록', 'PR 열림', 'main 머지', '/promote', 'prod PR', 'prod 반영'];
const STEP_AT: Record<string, number> = { pr_open: 1, foreign_commit: 1, main_merged: 2, promote_requested: 3, promote_blocked: 3, promote_pr_open: 4, prod_merged: 5 };
/** 진행 단계 위치 (MANIFEST_STEPS 의 index). prod 반영 없이 끝난 done 은 처음 단계로 둔다 */
export function manifestStep(b: LabelInput): number {
  if (b.status === 'done') return b.errorCode ? 0 : 5;
  return STEP_AT[b.status] ?? 0;
}

/** 묶음 상태 칩 문구: COMMIT_STALLED 는 '커밋 중'이 아니라 정지로 보인다 */
export const bundleStatusText = (status: string, errorCode: string | null | undefined, base: Record<string, string>) =>
  errorCode === 'COMMIT_STALLED' ? '커밋 정지' : base[status] ?? status;

/** 이력의 commit_error 캡션: 정지 원인이면 "재시도 예정"이라 쓰지 않는다 */
export const commitErrorCaption = (errorCode?: unknown) =>
  errorCode === 'FOREIGN_COMMIT' || errorCode === 'COMMIT_STALLED' ? '커밋 중 오류 — 정지, 확인 후 다시 시도 필요' : '커밋 중 오류(재시도 예정)';

/** 오류 코드의 사람 문구 (모르는 코드는 그대로) */
export const errorText = (code?: string | null) => (code ? ERROR[code] ?? code : '알 수 없음');
